import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import ComplianceDashboard from './components/ComplianceDashboard';
import ViolationDetectionPanel from './components/ViolationDetectionPanel';
import RemediationWorkflowPanel from './components/RemediationWorkflowPanel';
import PredictiveAnalyticsPanel from './components/PredictiveAnalyticsPanel';
import DeadlineTrackingPanel from './components/DeadlineTrackingPanel';
import openaiComplianceService from '../../services/openaiComplianceService';
import claudeComplianceService from '../../services/claudeComplianceService';
import remediationService from '../../services/remediationService';

const AIPoweredPayrollComplianceAnalyzer = () => {
  const [activeTab, setActiveTab] = useState('violations');
  const [loading, setLoading] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [violations, setViolations] = useState([]);
  const [remediations, setRemediations] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [autoScanEnabled, setAutoScanEnabled] = useState(true);
  const [lastScanTime, setLastScanTime] = useState(null);
  const [aiProvider, setAiProvider] = useState('openai');
  const [claudeAnalysis, setClaudeAnalysis] = useState(null);

  useEffect(() => {
    loadViolations();
    loadRemediations();
  }, []);

  useEffect(() => {
    if (autoScanEnabled) {
      const interval = setInterval(() => {
        handleAutomatedScan();
      }, 300000); // Every 5 minutes
      return () => clearInterval(interval);
    }
  }, [autoScanEnabled]);

  const loadViolations = async () => {
    try {
      const data = await remediationService?.getAllViolations();
      setViolations(data);
    } catch (error) {
      console.error('Failed to load violations:', error);
    }
  };

  const loadRemediations = async () => {
    try {
      const data = await remediationService?.getAllRemediationActions();
      setRemediations(data);
    } catch (error) {
      console.error('Failed to load remediations:', error);
    }
  };

  const handleAutomatedScan = async () => {
    try {
      setLoading(true);
      const results = await openaiComplianceService?.scanPayrollBeforeDeadline({
        status: 'pending_approval'
      });
      setScanResults(results);
      setLastScanTime(new Date());
      await loadViolations();
    } catch (error) {
      console.error('Automated scan failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualScan = async () => {
    try {
      setLoading(true);
      const results = await openaiComplianceService?.scanPayrollBeforeDeadline();
      setScanResults(results);
      setLastScanTime(new Date());
      await loadViolations();
    } catch (error) {
      console.error('Manual scan failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRemediation = async (violationIds) => {
    try {
      setLoading(true);
      const selectedViolations = violations?.filter(v => violationIds?.includes(v?.id));
      
      let result;
      if (aiProvider === 'claude') {
        result = await claudeComplianceService?.generateAdvancedRemediation(selectedViolations, {
          organizationType: 'PNG Business',
          complianceHistory: violations?.length,
          urgency: 'high'
        });
      } else {
        result = await openaiComplianceService?.generateRemediationSteps(selectedViolations);
      }
      
      await loadRemediations();
      return result;
    } catch (error) {
      console.error('Failed to generate remediation:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPredictions = async () => {
    try {
      setLoading(true);
      const historicalData = violations?.slice(0, 50);
      
      let result;
      if (aiProvider === 'claude') {
        result = await claudeComplianceService?.forecastComplianceRisks(historicalData, '90 days');
      } else {
        result = await openaiComplianceService?.getPredictiveCompliance(historicalData);
      }
      
      setPredictions(result);
    } catch (error) {
      console.error('Failed to load predictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaudePatternDetection = async () => {
    try {
      setLoading(true);
      let result = await claudeComplianceService?.detectViolationPatterns(violations);
      setClaudeAnalysis(result);
    } catch (error) {
      console.error('Failed to detect patterns with Claude:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaudeRegulationInterpretation = async (regulationText) => {
    try {
      setLoading(true);
      let result = await claudeComplianceService?.interpretRegulation(regulationText, {
        industry: 'General Business',
        location: 'Papua New Guinea'
      });
      setClaudeAnalysis(result);
    } catch (error) {
      console.error('Failed to interpret regulation:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredViolations = violations?.filter(v => {
    if (filterSeverity !== 'all' && v?.severity !== filterSeverity) return false;
    if (filterDepartment !== 'all' && v?.department !== filterDepartment) return false;
    return true;
  });

  const tabs = [
    { id: 'violations', label: 'Violation Detection', icon: 'AlertTriangle' },
    { id: 'remediation', label: 'Remediation Workflows', icon: 'CheckSquare' },
    { id: 'predictive', label: 'Predictive Analytics', icon: 'TrendingUp' },
    { id: 'deadlines', label: 'Deadline Tracking', icon: 'Clock' }
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Sparkles" size={32} color="var(--color-primary)" />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
              AI-Powered Payroll Compliance Analyzer
            </h1>
          </div>
          <p className="caption text-sm md:text-base text-muted-foreground">
            Intelligent automated violation detection and AI-generated remediation guidance for proactive compliance management
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* AI Analysis Dashboard - 4 cols */}
          <div className="lg:col-span-4">
            <ComplianceDashboard 
              scanResults={scanResults}
              violations={violations}
              loading={loading}
              onManualScan={handleManualScan}
              autoScanEnabled={autoScanEnabled}
              onToggleAutoScan={() => setAutoScanEnabled(!autoScanEnabled)}
              lastScanTime={lastScanTime}
            />
          </div>

          {/* Main Analysis Panel - 8 cols */}
          <div className="lg:col-span-8">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {/* Tabs */}
              <div className="border-b border-border">
                <div className="flex items-center gap-1 p-2">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} className="inline mr-2" />
                      {tab?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="p-4 border-b border-border bg-muted/30">
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e?.target?.value)}
                    options={severityOptions}
                    className="w-48"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Filter"
                    onClick={() => {
                      setFilterSeverity('all');
                      setFilterDepartment('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    <Icon name="Info" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">
                      {filteredViolations?.length} violations found
                    </span>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'violations' && (
                  <ViolationDetectionPanel
                    violations={filteredViolations}
                    scanResults={scanResults}
                    loading={loading}
                    onGenerateRemediation={handleGenerateRemediation}
                  />
                )}
                {activeTab === 'remediation' && (
                  <RemediationWorkflowPanel
                    remediations={remediations}
                    violations={violations}
                    loading={loading}
                    onRefresh={loadRemediations}
                  />
                )}
                {activeTab === 'predictive' && (
                  <PredictiveAnalyticsPanel
                    predictions={predictions}
                    loading={loading}
                    onLoadPredictions={handleLoadPredictions}
                  />
                )}
                {activeTab === 'deadlines' && (
                  <DeadlineTrackingPanel
                    violations={violations}
                    scanResults={scanResults}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIPoweredPayrollComplianceAnalyzer;